import { type CSSProperties, useLayoutEffect, useRef, useState } from 'react';

type CommonProps = {
  /** Rotation degrees (default -6 — natural pen tilt). */
  rot?: number;
  color?: string;
  strokeWidth?: number;
  /** Animation start delay in ms. */
  delay?: number;
  className?: string;
};

type FixedProps = CommonProps & {
  auto?: false;
  /** Centre-x: number = px from parent's left, '50%' = horizontally centred. */
  cx?: number | string;
  /** Centre-y: number = px from parent's top, '50%' = vertically centred. */
  cy?: number | string;
  rx: number;
  ry: number;
};

type AutoProps = CommonProps & {
  auto: true;
  /** Horizontal padding around content (px). Default 8. */
  padX?: number;
  /** Vertical padding around content (px). Default 3. */
  padY?: number;
};

export type InkCircleProps = FixedProps | AutoProps;

/**
 * Hand-drawn ink ellipse used to ring an active option in the briefing
 * settings panel. Stroke is dasharray-animated via `circle-draw` so it
 * pencils-in instead of just appearing.
 *
 * Two modes:
 * - **fixed** (default): explicit `cx/cy/rx/ry`. Mixed numeric/string
 *   anchors implement the cross-centre cases the dossier uses.
 * - **auto**: pass `auto` and the ellipse measures its `position:relative`
 *   parent and sizes to wrap the content + `padX`/`padY` padding.
 *   ResizeObserver keeps it accurate as text reflows.
 */
export const InkCircle = (props: InkCircleProps) => {
  if (props.auto) return <InkCircleAuto {...props} />;
  return <InkCircleFixed {...props} />;
};

const InkCircleFixed = ({
  cx,
  cy,
  rx,
  ry,
  rot = -6,
  color = 'var(--color-magenta)',
  strokeWidth = 4,
  delay = 0,
  className,
}: FixedProps) => {
  const w = rx * 2 + 16;
  const h = ry * 2 + 16;
  const cxIsNum = typeof cx === 'number';
  const cyIsNum = typeof cy === 'number';
  const style: CSSProperties = {
    position: 'absolute',
    width: w,
    height: h,
    pointerEvents: 'none',
    overflow: 'visible',
    transformOrigin: 'center',
  };
  let transform = `rotate(${rot}deg)`;
  if (cxIsNum) style.left = (cx as number) - rx - 8;
  else {
    style.left = '50%';
    transform = `translateX(-50%) ${transform}`;
  }
  if (cyIsNum) style.top = (cy as number) - ry - 8;
  else {
    style.top = '50%';
    transform = `translateY(-50%) ${transform}`;
  }
  style.transform = transform;
  return (
    <svg
      className={className}
      style={style}
      viewBox={`0 0 ${w} ${h}`}
      role="presentation"
      aria-hidden="true"
    >
      <title>ink ring</title>
      <ellipse
        cx={rx + 8}
        cy={ry + 8}
        rx={rx}
        ry={ry}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray="360"
        strokeDashoffset="360"
        style={{ animation: `circle-draw 700ms ${delay}ms cubic-bezier(.4,.1,.2,1) forwards` }}
      />
    </svg>
  );
};

const InkCircleAuto = ({
  padX = 8,
  padY = 3,
  rot = -6,
  color = 'var(--color-magenta)',
  strokeWidth = 3,
  delay = 0,
  className,
}: AutoProps) => {
  const ref = useRef<SVGSVGElement>(null);
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  useLayoutEffect(() => {
    const svg = ref.current;
    const parent = svg?.parentElement;
    if (!parent) return;
    const measure = (): void => {
      const r = parent.getBoundingClientRect();
      setSize({ w: r.width, h: r.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(parent);
    return () => ro.disconnect();
  }, []);

  const buf = strokeWidth + 4;
  const W = size.w + padX * 2;
  const H = size.h + padY * 2;
  const svgW = W + buf * 2;
  const svgH = H + buf * 2;

  return (
    <svg
      ref={ref}
      className={className}
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: svgW,
        height: svgH,
        marginLeft: -svgW / 2,
        marginTop: -svgH / 2,
        pointerEvents: 'none',
        overflow: 'visible',
        transform: `rotate(${rot}deg)`,
        transformOrigin: 'center',
        opacity: size.w > 0 ? 1 : 0,
      }}
      viewBox={`0 0 ${svgW} ${svgH}`}
      role="presentation"
      aria-hidden="true"
    >
      <title>ink ring</title>
      <ellipse
        cx={svgW / 2}
        cy={svgH / 2}
        rx={W / 2}
        ry={H / 2}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray="360"
        strokeDashoffset="360"
        style={{ animation: `circle-draw 700ms ${delay}ms cubic-bezier(.4,.1,.2,1) forwards` }}
      />
    </svg>
  );
};
